import { OfficeLayout, OfficeLocation } from '../types';
import { findOverlappingRoom } from './isoMath';
// ── SECCIÓN: Import de tipos — fuente única de verdad (types/systemHealth.ts) ──
import {
  SystemHealthStatus,
  SystemHealthWeights,
  SystemHealthConfig,
  SystemIncident,
  SystemHealthMetrics,
} from '../types/systemHealth';

/*type SystemHealthStatus = 'optimal' | 'good' | 'degraded' | 'critical';

interface SystemHealthWeights {
  structural: number;
  spatial: number;
  performance: number;
  agentActivity: number;
}

interface SystemHealthConfig {
  preset: 'balanced' | 'strict' | 'relaxed' | string;
  minAreaPerAgentM2: number;
  maxRoomOccupancyPercent: number;
  maxAgentIdlePercent: number;
  minEnergyAlertLevel: number;
  targetFps: number;
  maxTickLatencyMs: number;
  allowRoomOverlap: boolean;
  checkWalkableAccess: boolean;
  weights: SystemHealthWeights;
  showLivePulseAnimation: boolean;
  highlightDegradedZones: boolean;
  notifyOnStatusChange: boolean;
  compactHudMode: boolean;
}

interface SystemIncident {
  id: string;
  level: 'info' | 'warning' | 'critical';
  title: string;
  message: string;
  category: 'structural' | 'spatial' | 'performance' | 'agents';
  timestamp: number;
  locationId?: string;
  locationName?: string;
  roomId?: string;
}

interface SystemHealthMetrics {
  overallScore: number;
  status: SystemHealthStatus;
  statusLabel: string;
  statusColor: string;
  statusBadgeBg: string;
  statusBorderColor: string;
  structuralScore: number;
  spatialScore: number;
  performanceScore: number;
  activityScore: number;
  totalAgentsCount: number;
  placedAgentsCount: number;
  activeAgentsCount: number;
  idleAgentsCount: number;
  lowEnergyAgentsCount: number;
  totalFloorAreaM2: number;
  areaPerPlacedAgentM2: number;
  occupancyPercent: number;
  roomOverlapCount: number;
  conflictingRooms: string[];
  measuredFps: number;
  estimatedTickLatencyMs: number;
  incidents: SystemIncident[];
  lastCalculatedAt: number;
}*/

export const HEALTH_CONFIG_STORAGE_KEY = 'agentoffice_system_health_config_v1';

export const DEFAULT_HEALTH_CONFIG: SystemHealthConfig = {
  preset: 'balanced',
  minAreaPerAgentM2: 4.0,
  maxRoomOccupancyPercent: 85,
  maxAgentIdlePercent: 40,
  minEnergyAlertLevel: 20,
  targetFps: 60,
  maxTickLatencyMs: 35,
  allowRoomOverlap: false,
  checkWalkableAccess: true,
  weights: {
    structural: 30,
    spatial: 30,
    performance: 20,
    agentActivity: 20,
  },
  showLivePulseAnimation: true,
  highlightDegradedZones: true,
  notifyOnStatusChange: true,
  compactHudMode: false,
};

export const STRICT_HEALTH_PRESET: SystemHealthConfig = {
  preset: 'strict',
  minAreaPerAgentM2: 6.0,
  maxRoomOccupancyPercent: 70,
  maxAgentIdlePercent: 25,
  minEnergyAlertLevel: 30,
  targetFps: 60,
  maxTickLatencyMs: 20,
  allowRoomOverlap: false,
  checkWalkableAccess: true,
  weights: {
    structural: 35,
    spatial: 25,
    performance: 25,
    agentActivity: 15,
  },
  showLivePulseAnimation: true,
  highlightDegradedZones: true,
  notifyOnStatusChange: true,
  compactHudMode: false,
};

export const RELAXED_HEALTH_PRESET: SystemHealthConfig = {
  preset: 'relaxed',
  minAreaPerAgentM2: 2.5,
  maxRoomOccupancyPercent: 95,
  maxAgentIdlePercent: 60,
  minEnergyAlertLevel: 10,
  targetFps: 45,
  maxTickLatencyMs: 50,
  allowRoomOverlap: true,
  checkWalkableAccess: false,
  weights: {
    structural: 20,
    spatial: 30,
    performance: 20,
    agentActivity: 30,
  },
  showLivePulseAnimation: true,
  highlightDegradedZones: false,
  notifyOnStatusChange: false,
  compactHudMode: true,
};

export function loadSavedHealthConfig(): SystemHealthConfig {
  try {
    const raw = localStorage.getItem(HEALTH_CONFIG_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        ...DEFAULT_HEALTH_CONFIG,
        ...parsed,
        weights: {
          ...DEFAULT_HEALTH_CONFIG.weights,
          ...(parsed.weights || {}),
        },
      };
    }
  } catch (e) {
    console.warn('Failed to load system health config from localStorage', e);
  }
  return { ...DEFAULT_HEALTH_CONFIG };
}

export const loadHealthConfig = loadSavedHealthConfig;

export function saveHealthConfig(config: SystemHealthConfig): void {
  try {
    localStorage.setItem(HEALTH_CONFIG_STORAGE_KEY, JSON.stringify(config));
  } catch (e) {
    console.warn('Failed to save system health config to localStorage', e);
  }
}

export const saveSystemHealthConfig = saveHealthConfig;

/**
 * Computes real-time system health metrics based on configuration and current office state
 */
export function calculateSystemHealth(
  activeLocation?: OfficeLocation | OfficeLayout | null,
  allLocations?: OfficeLocation[],
  currentFps: number = 60,
  config: SystemHealthConfig = DEFAULT_HEALTH_CONFIG
): SystemHealthMetrics {
  const safeConfig = {
    ...DEFAULT_HEALTH_CONFIG,
    ...(config || {}),
    weights: {
      ...DEFAULT_HEALTH_CONFIG.weights,
      ...(config?.weights || {}),
    },
  };

  const incidents: SystemIncident[] = [];
  const now = Date.now();

  if (!activeLocation) {
    return {
      overallScore: 100,
      status: 'optimal',
      statusLabel: 'Optimal',
      statusColor: '#10b981',
      statusBadgeBg: 'rgba(6, 78, 59, 0.85)',
      statusBorderColor: '#059669',
      structuralScore: 100,
      spatialScore: 100,
      performanceScore: 100,
      activityScore: 100,
      totalAgentsCount: 0,
      placedAgentsCount: 0,
      activeAgentsCount: 0,
      idleAgentsCount: 0,
      lowEnergyAgentsCount: 0,
      totalFloorAreaM2: 100,
      areaPerPlacedAgentM2: 100,
      occupancyPercent: 0,
      roomOverlapCount: 0,
      conflictingRooms: [],
      measuredFps: 60,
      estimatedTickLatencyMs: 16,
      incidents: [],
      lastCalculatedAt: now,
    };
  }

  const agents = activeLocation.agents || [];
  const rooms = activeLocation.rooms || [];
  const items = activeLocation.items || [];

  const tileSize = activeLocation.tileSizeMeters ?? 1.0;
  const gridW = activeLocation.gridWidth || 20;
  const gridH = activeLocation.gridHeight || 20;
  const totalFloorAreaM2 = (gridW * tileSize) * (gridH * tileSize);

  // 1. Structural Integrity (Room overlaps, grid bounds)
  let structuralScore = 100;
  let roomOverlapCount = 0;
  const conflictingRooms: string[] = [];

  for (let i = 0; i < rooms.length; i++) {
    const roomA = rooms[i];
    const otherRooms = rooms.filter((_, idx) => idx !== i);
    const overlap = findOverlappingRoom(roomA, otherRooms);
    if (overlap) {
      roomOverlapCount++;
      if (conflictingRooms.indexOf(roomA.name) === -1) conflictingRooms.push(roomA.name);
if (conflictingRooms.indexOf(overlap.name) === -1) conflictingRooms.push(overlap.name);
    }
  }

  if (roomOverlapCount > 0) {
    if (!safeConfig.allowRoomOverlap) {
      const penalty = Math.min(60, roomOverlapCount * 30);
      structuralScore = Math.max(10, structuralScore - penalty);
      incidents.push({
        id: `overlap-${now}`,
        level: 'critical',
        title: 'Zone Overlap Detected',
        message: `There are ${roomOverlapCount} overlap conflicts between zones (${conflictingRooms.slice(0, 3).join(', ')}).`,
        category: 'structural',
        timestamp: now,
        locationId: activeLocation.id,
        locationName: activeLocation.name,
      });
    } else {
      structuralScore = Math.max(50, structuralScore - (roomOverlapCount * 10));
      incidents.push({
        id: `overlap-tol-${now}`,
        level: 'warning',
        title: 'Overlap in Tolerant Mode',
        message: `${roomOverlapCount} overlapping zones permitted per flexible configuration.`,
        category: 'structural',
        timestamp: now,
      });
    }
  }

  // 2. Spatial Density & Capacity (m² per agent and room occupancy)
  let spatialScore = 100;
  const placedAgents = agents.filter((a) => a.isPlaced !== false);
  const placedAgentsCount = placedAgents.length;
  const areaPerPlacedAgentM2 = placedAgentsCount > 0 ? totalFloorAreaM2 / placedAgentsCount : totalFloorAreaM2;

  if (placedAgentsCount > 0) {
    const minRequiredArea = safeConfig.minAreaPerAgentM2;
    if (areaPerPlacedAgentM2 < minRequiredArea) {
      const ratio = areaPerPlacedAgentM2 / minRequiredArea; // < 1.0
      const spatialPenalty = Math.round((1 - ratio) * 60);
      spatialScore = Math.max(20, 100 - spatialPenalty);

      incidents.push({
        id: `density-${now}`,
        level: areaPerPlacedAgentM2 < minRequiredArea * 0.5 ? 'critical' : 'warning',
        title: 'High Agent Density',
        message: `Available space per agent: ${areaPerPlacedAgentM2.toFixed(1)} m² (Configured minimum threshold: ${minRequiredArea.toFixed(1)} m²).`,
        category: 'spatial',
        timestamp: now,
        locationId: activeLocation.id,
        locationName: activeLocation.name,
      });
    }
  }

  // Check individual room capacity
  for (const room of rooms) {
    const roomArea = room.width * room.height * (tileSize * tileSize);
    // Count agents in this room
    const agentsInRoom = agents.filter(
      (a) =>
        a.currentCol >= room.col &&
        a.currentCol < room.col + room.width &&
        a.currentRow >= room.row &&
        a.currentRow < room.row + room.height
    ).length;

    if (agentsInRoom > 0) {
      const roomAreaPerAgent = roomArea / agentsInRoom;
      if (roomAreaPerAgent < safeConfig.minAreaPerAgentM2 * 0.6) {
        spatialScore = Math.max(25, spatialScore - 15);
        incidents.push({
          id: `room-density-${room.id}`,
          level: 'warning',
          title: `Capacity Exceeded in ${room.name}`,
          message: `${agentsInRoom} agents concentrated in ${roomArea.toFixed(0)} m² (${roomAreaPerAgent.toFixed(1)} m²/agent).`,
          category: 'spatial',
          timestamp: now,
          roomId: room.id,
          locationId: activeLocation.id,
        });
      }
    }
  }

  // 3. Performance and Simulation Ticks
  let performanceScore = 100;
  const measuredFps = Math.max(1, Math.min(120, currentFps));
  const targetFps = safeConfig.targetFps || 60;
  const fpsRatio = Math.min(1, measuredFps / targetFps);
  const estimatedTickLatencyMs = Math.round(1000 / measuredFps);

  if (fpsRatio < 0.85) {
    performanceScore = Math.max(20, Math.round(fpsRatio * 100));
    incidents.push({
      id: `perf-fps-${now}`,
      level: fpsRatio < 0.5 ? 'critical' : 'warning',
      title: 'Reduced Simulation Tick Rate',
      message: `Current performance: ${measuredFps.toFixed(0)} FPS (~${estimatedTickLatencyMs}ms cycle latency, target: ${targetFps} FPS).`,
      category: 'performance',
      timestamp: now,
    });
  }

  // 4. Agent Activity, Productivity & Energy
  let activityScore = 100;
  let activeAgentsCount = 0;
  let idleAgentsCount = 0;
  let lowEnergyAgentsCount = 0;

  if (placedAgentsCount > 0) {
    for (const a of agents) {
      if (a.status === 'idle') {
        idleAgentsCount++;
      } else {
        activeAgentsCount++;
      }
      if ((a.energy ?? 100) < safeConfig.minEnergyAlertLevel) {
        lowEnergyAgentsCount++;
      }
    }

    const idlePercent = (idleAgentsCount / placedAgentsCount) * 100;
    if (idlePercent > safeConfig.maxAgentIdlePercent) {
      const excess = idlePercent - safeConfig.maxAgentIdlePercent;
      activityScore = Math.max(30, Math.round(100 - (excess * 1.2)));
      incidents.push({
        id: `agents-idle-${now}`,
        level: idlePercent > 70 ? 'warning' : 'info',
        title: 'High Agent Idle Rate',
        message: `${idlePercent.toFixed(0)}% of agents are idle/standby (Tolerance threshold: ${safeConfig.maxAgentIdlePercent}%).`,
        category: 'agents',
        timestamp: now,
      });
    }

    if (lowEnergyAgentsCount > 0) {
      const lowEnergyPercent = (lowEnergyAgentsCount / placedAgentsCount) * 100;
      if (lowEnergyPercent > 30) {
        activityScore = Math.max(35, activityScore - 15);
        incidents.push({
          id: `agents-energy-${now}`,
          level: 'warning',
          title: 'Agents with Critical Energy',
          message: `${lowEnergyAgentsCount} agents have energy levels below ${safeConfig.minEnergyAlertLevel}%.`,
          category: 'agents',
          timestamp: now,
        });
      }
    }
  }

  // Global Weighted Health Score
  const totalWeight =
    (safeConfig.weights.structural || 30) +
    (safeConfig.weights.spatial || 30) +
    (safeConfig.weights.performance || 20) +
    (safeConfig.weights.agentActivity || 20);

  const wStruct = ((safeConfig.weights.structural || 30) / totalWeight);
  const wSpatial = ((safeConfig.weights.spatial || 30) / totalWeight);
  const wPerf = ((safeConfig.weights.performance || 20) / totalWeight);
  const wAct = ((safeConfig.weights.agentActivity || 20) / totalWeight);

  const rawOverall =
    (structuralScore * wStruct) +
    (spatialScore * wSpatial) +
    (performanceScore * wPerf) +
    (activityScore * wAct);

  const overallScore = Math.round(Math.max(0, Math.min(100, rawOverall)));

  // Determine Category and Styles
  let status: SystemHealthStatus = 'optimal';
  let statusLabel = 'Optimal';
  let statusColor = '#10b981'; // Emerald
  let statusBadgeBg = 'rgba(6, 78, 59, 0.85)';
  let statusBorderColor = '#059669';

  if (overallScore < 50 || structuralScore < 30) {
    status = 'critical';
    statusLabel = 'Critical';
    statusColor = '#f43f5e'; // Rose
    statusBadgeBg = 'rgba(136, 19, 55, 0.9)';
    statusBorderColor = '#e11d48';
  } else if (overallScore < 75 || roomOverlapCount > 0 || spatialScore < 50) {
    status = 'degraded';
    statusLabel = 'Degraded';
    statusColor = '#f59e0b'; // Amber
    statusBadgeBg = 'rgba(120, 53, 15, 0.9)';
    statusBorderColor = '#d97706';
  } else if (overallScore < 90) {
    status = 'good';
    statusLabel = 'Healthy';
    statusColor = '#06b6d4'; // Cyan
    statusBadgeBg = 'rgba(8, 51, 68, 0.85)';
    statusBorderColor = '#0891b2';
  }

  const occupancyPercent = placedAgentsCount > 0
    ? Math.min(100, Math.round((placedAgentsCount * safeConfig.minAreaPerAgentM2 / Math.max(1, totalFloorAreaM2)) * 100))
    : 0;

  return {
    overallScore,
    status,
    statusLabel,
    statusColor,
    statusBadgeBg,
    statusBorderColor,
    structuralScore,
    spatialScore,
    performanceScore,
    activityScore,
    totalAgentsCount: agents.length,
    placedAgentsCount,
    activeAgentsCount,
    idleAgentsCount,
    lowEnergyAgentsCount,
    totalFloorAreaM2,
    areaPerPlacedAgentM2,
    occupancyPercent,
    roomOverlapCount,
    conflictingRooms,
    measuredFps,
    estimatedTickLatencyMs,
    incidents,
    lastCalculatedAt: now,
  };
}

export function computeSystemHealthMetrics(
  activeLocation?: OfficeLocation | OfficeLayout | null,
  config: SystemHealthConfig = DEFAULT_HEALTH_CONFIG,
  currentFps: number = 60
): SystemHealthMetrics {
  return calculateSystemHealth(activeLocation, undefined, currentFps, config);
}
